import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { trackProductEvent } from "@/lib/productAnalytics";
import { isNumericScore, scoreToRating, scoreToTone, scoreValueToRating, sortScoreMetrics } from "@/lib/scoreGrade";
import { relationshipBaseline, relationshipUpdateSignals } from "@/lib/relationshipDelta";
import { shouldShowConversationNavigation } from "@/lib/conversationNavigation";
import { formatPlainTranscriptSpeakers, transcriptSpeakerPresentation } from "@/lib/transcriptSpeaker";
import Nav from "@/components/Nav";
import ScoreInsight from "@/components/ScoreInsight";
import WorkingHourglass from "@/components/WorkingHourglass";
import { toast } from "sonner";
import {
  Play,
  Pause,
  Copy,
  Sparkle,
  ArrowClockwise,
  ClipboardText,
  ChatCircle,
  Brain,
  Target,
  MagnifyingGlass,
  ChartBar,
  Warning,
  Lightbulb,
  Flag,
  TrendUp,
  ClockCounterClockwise,
  WarningCircle,
  CheckCircle,
  Trash,
  X,
  UserCircle,
  Megaphone,
  Barbell,
  ArrowCircleUpRight,
  ChatsCircle,
  Handshake,
  CaretDown,
  ArrowLeft,
  ArrowRight,
  PhoneCall,
  PencilSimple,
  Check,
} from "@phosphor-icons/react";

const STYLES = [
  { key: "first_person", label: "First-Person" },
  { key: "salesforce", label: "Salesforce" },
  { key: "hubspot", label: "HubSpot" },
  { key: "close", label: "Close" },
  { key: "minimal", label: "Minimal" },
  { key: "executive", label: "Executive" },
  { key: "detailed_history", label: "Detailed History" },
];

function fmt(t) {
  if (t == null) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function fmtDateTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normalizedMetadataValue(value) {
  return String(value || "").trim().replace(/\s+/g, " ").toLocaleLowerCase();
}

// Normalize a confidence value into 'high' | 'medium' | 'low'
function confLevel(c) {
  if (c == null) return null;
  if (typeof c === "number") {
    if (c >= 75) return "high";
    if (c >= 50) return "medium";
    return "low";
  }
  const s = String(c).toLowerCase();
  if (s === "high" || s === "medium" || s === "low") return s;
  return null;
}

function ConfChip({ confidence, testId, inline = false }) {
  const lvl = confLevel(confidence);
  if (lvl !== "medium") return null;

  return (
    <span
      data-testid={testId}
      className={`confidence-chip confidence-chip--uncertain${inline ? " is-inline" : ""}`}
      title="Less certain"
      aria-label="Less certain"
      tabIndex={0}
    >
      ?
    </span>
  );
}

// Extract text + confidence from either a plain string or a {text, confidence} object
function itemParts(it) {
  if (it == null) return { text: "", confidence: null };
  if (typeof it === "string") {
    return { text: localizeSellerRole(it), confidence: null };
  }
  if (typeof it === "object") {
    return {
      text: localizeSellerRole(it.text || it.description || JSON.stringify(it)),
      confidence: it.confidence,
    };
  }
  return { text: localizeSellerRole(String(it)), confidence: null };
}

function localizeSellerRole(value) {
  if (typeof value !== "string") return value;

  const normalized = value
    .replace(/\bDer Commercial\b/g, "Der Vertriebsmitarbeiter")
    .replace(/\bder Commercial\b/g, "der Vertriebsmitarbeiter")
    .replace(/\bDen Commercial\b/g, "Den Vertriebsmitarbeiter")
    .replace(/\bden Commercial\b/g, "den Vertriebsmitarbeiter")
    .replace(/\bDem Commercial\b/g, "Dem Vertriebsmitarbeiter")
    .replace(/\bdem Commercial\b/g, "dem Vertriebsmitarbeiter")
    .replace(/\bEin Commercial\b/g, "Ein Vertriebsmitarbeiter")
    .replace(/\bein Commercial\b/g, "ein Vertriebsmitarbeiter")
    .replace(/\bEinen Commercial\b/g, "Einen Vertriebsmitarbeiter")
    .replace(/\beinen Commercial\b/g, "einen Vertriebsmitarbeiter");

  const englishNormalized = normalized
    .replace(/\b(the|a|an)\s+commercial\b/gi, (_, article) => `${article} rep`)
    .replace(/\bcommercial(?=\s+(?:must|should|will|needs|committed|said|asked|offered|agreed|clarified|followed|handled|missed|could|would)\b)/gi, "rep");
  if (!/(?:sales[ _-]?rep|sales representative|account manager|salesperson|sales agent)/i.test(englishNormalized)) return englishNormalized;
  const lower = englishNormalized.toLocaleLowerCase();
  let role = "rep";
  if (/\b(?:il|lo|la|cliente|impegno|impegnat|verificare|aggiornare|possibilit[aà]|pomeriggio)\b/i.test(lower)) {
    role = "commerciale";
  } else if (/\b(?:le|la|client|engagement|vérifier|mettre à jour|commerciale?)\b/i.test(lower)) {
    role = "commercial";
  } else if (/\b(?:el|la|cliente|compromiso|verificar|actualizar|comercial)\b/i.test(lower)) {
    role = "representante comercial";
  } else if (/\b(?:der|die|kunde|zusage|prüfen|aktualisieren|vertrieb)\b/i.test(lower)) {
    role = "Vertriebsmitarbeiter";
  }
  return englishNormalized.replace(/(?:sales[ _-]?rep|sales representative|account manager|salesperson|sales agent)/gi, role);
}

function signalValue(value) {
  if (value == null || value === "") return "Not established";
  if (typeof value === "object") {
    if (value.text != null && value.text !== "") return String(value.text);
    if (value.score != null) return scoreToRating(value.score, { fallback: "Not established" });
    return scoreValueToRating(value.value);
  }
  return scoreValueToRating(value);
}

function signalScoreToneClass(value, { muted = false, invert = false } = {}) {
  let score = value;
  if (value && typeof value === "object") {
    if (value.text != null && value.text !== "") return muted ? "text-slate-500" : "";
    score = value.score ?? value.value;
  }
  if (!isNumericScore(score)) return muted ? "text-slate-500" : "";
  return `score-text score-text--${scoreToTone(score, { invert })}`;
}

function LongitudinalDelta({ update, analysis, relationshipPath, relationshipName, relationshipReturnState }) {
  const [expanded, setExpanded] = useState(true);
  if (!update) return null;

  const isComparison = Boolean(update.previous_call_id);
  const visibleSignals = isComparison
    ? relationshipUpdateSignals(update)
    : relationshipBaseline(analysis);
  const title = isComparison ? "Relationship update" : "Relationship Baseline";

  return (
    <section
      className={`longitudinal-delta longitudinal-delta--compact${expanded ? " is-expanded" : " is-collapsed"}`}
      data-testid="longitudinal-call-delta"
    >
      <div className="longitudinal-delta__header">
        <button
          type="button"
          className="longitudinal-delta__toggle"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          aria-controls="relationship-context-details"
        >
          <span className="longitudinal-delta__title">
            <ClockCounterClockwise size={17} weight="duotone" />
            {title}
          </span>
        </button>

        <div className="longitudinal-delta__header-actions">
          {relationshipPath && (
            <Link
              to={relationshipPath}
              state={relationshipReturnState}
              className="longitudinal-delta__relationship-link"
              aria-label={`View relationship for ${relationshipName || "this customer"}`}
            >
              <span>View relationship</span>
              <ArrowRight size={14} weight="bold" aria-hidden="true" />
            </Link>
          )}
          <button
            type="button"
            className="longitudinal-delta__expand-button"
            onClick={() => setExpanded((current) => !current)}
            aria-expanded={expanded}
            aria-controls="relationship-context-details"
            aria-label={expanded ? `Collapse ${title}` : `Expand ${title}`}
          >
            <CaretDown
              size={17}
              weight="bold"
              className="longitudinal-delta__chevron"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {expanded && (
        <div id="relationship-context-details" className="longitudinal-delta__content">
          {visibleSignals.length > 0 && (
            <div
              className="longitudinal-signal-grid"
              style={{ "--longitudinal-signal-count": visibleSignals.length }}
              aria-label={isComparison ? "Updated relationship signals" : "Relationship baseline signals"}
            >
              {visibleSignals.map(([label, signal]) => {
                const invert = label === "Churn risk";
                return (
                  <div key={label} className="longitudinal-signal">
                    <div className="longitudinal-signal__label">{label}</div>
                    <div className="longitudinal-signal__value">
                      {isComparison ? (
                        <>
                          <span className={signalScoreToneClass(signal.from, { muted: true, invert })}>{signalValue(signal.from)}</span>
                          <span className="mx-2 text-violet-400">→</span>
                          <span className={signalScoreToneClass(signal.to, { invert })}>{signalValue(signal.to)}</span>
                        </>
                      ) : (
                        <span className={signalScoreToneClass(signal, { invert })}>{signalValue(signal)}</span>
                      )}
                    </div>
                    {isComparison && signal.direction && signal.direction !== "unknown" && (
                      <div className="longitudinal-signal__direction">{signal.direction}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {visibleSignals.length === 0 && (
            <p className="longitudinal-delta__empty">
              {isComparison ? "No material relationship changes in this call." : "No baseline signals established yet."}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

const SCORE_LABELS = {
  human_writing: "Human writing",
  template_language: "Template language",
  business_reasoning: "Business reasoning",
  negotiation_insight: "Negotiation insight",
  emotional_accuracy: "Emotional accuracy",
  customer_psychology: "Customer psychology",
  future_usefulness: "Future usefulness",
  writing_dna_similarity: "Writing DNA match",
};

const SCORE_DESCRIPTIONS = {
  human_writing: "How natural and human the note sounds, without formulaic or AI-like phrasing.",
  template_language: "How well the note avoids generic, repetitive, or canned CRM language.",
  business_reasoning: "How clearly the note explains the commercial logic, decisions, and cause-and-effect behind the conversation.",
  negotiation_insight: "How well the note captures objections, leverage, trade-offs, and decision dynamics.",
  emotional_accuracy: "How faithfully the note reflects tone, reactions, tension, rapport, and conversational subtext.",
  customer_psychology: "How clearly the note captures the customer's motivations, concerns, intent, and behavior.",
  future_usefulness: "How useful the note will be when preparing the next contact and deciding what to do next.",
  writing_dna_similarity: "How closely the note matches the user's learned writing style and reasoning patterns.",
};

function scoreTextClass(value, invert = false) {
  return `score-grade score-text score-text--${scoreTone(value, invert)}`;
}

function QualityScorePanel({ scores, updating = false }) {
  if (updating) {
    return (
      <section className="note-quality note-quality--updating" data-testid="quality-scores-updating" aria-live="polite">
        <div className="note-quality__header">
          <div>
            <div className="note-quality__title">Note quality</div>
            <p>How closely this version matches the Callsect writing and reasoning standard.</p>
          </div>
          <div className="note-quality__updating" role="status">
            <WorkingHourglass size={14} />
            <span>Updating…</span>
          </div>
        </div>
      </section>
    );
  }

  if (!scores) return null;
  const cats = Object.keys(SCORE_LABELS);
  const overall = scores.overall || 0;
  const weakCategories = cats.filter((key) => (scores[key]?.score || 0) < 70);

  return (
    <section className="note-quality" data-testid="quality-scores">
      <div className="note-quality__header">
        <div>
          <div className="note-quality__title">Note quality</div>
          <p>How closely this version matches the Callsect writing and reasoning standard.</p>
        </div>
        <div className="note-quality__overall">
          <span className={scoreTextClass(overall)} data-testid="overall-score">{scoreToRating(overall)}</span>
          <small>overall</small>
        </div>
      </div>

      <div className="note-quality__grid">
        {cats.map((key, index) => {
          const value = scores[key] || {};
          const score = Math.max(0, Math.min(100, value.score || 0));
          return (
            <div
              key={key}
              className={`note-quality__metric note-quality__metric--${scoreTone(score)}`}
              data-testid={`score-${key}`}
              style={{ animationDelay: `${index * 45}ms` }}
            >
              <div className="note-quality__metric-head">
                <div className="note-quality__metric-label">
                  <span>{SCORE_LABELS[key]}</span>
                  <ScoreInsight label={SCORE_LABELS[key]} description={SCORE_DESCRIPTIONS[key]} />
                </div>
              </div>
              <div className="score-meter">
                <div
                  className={`score-meter__fill score-meter__fill--${scoreTone(score)}`}
                  style={{ "--score-meter-scale": score / 100 }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {weakCategories.length > 0 && (
        <div className="note-quality__weak">
          <div className="note-quality__weak-title">Areas to strengthen</div>
          {weakCategories.map((key) => {
            const value = scores[key];
            return (
              <article key={key} data-testid={`weak-${key}`} className={`note-quality__weak-card note-quality__weak-card--${scoreTone(value.score || 0)}`}>
                <div className="note-quality__weak-head">
                  <span>{SCORE_LABELS[key]}</span>
                </div>
                {value.reason && (
                  <p><span>Reason</span>{value.reason}</p>
                )}
                {value.suggestion && (
                  <p><span className="text-emerald-400">Suggested improvement</span>{value.suggestion}</p>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function RevealLinesMark({ compact = false }) {
  return (
    <span className={`reveal-lines-mark${compact ? " reveal-lines-mark--compact" : ""}`} aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
    </span>
  );
}

export default function CallDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relationshipContext, setRelationshipContext] = useState(null);
  const requestedTab = searchParams.get("section");
  const deepDiveTarget = searchParams.get("deepDiveTarget") || location.state?.deepDiveTarget || null;
  const transcriptAt = searchParams.get("transcriptAt");
  const activeTab = ["notes", "transcript", "analysis", "coaching"].includes(requestedTab)
    ? requestedTab
    : "analysis";
  const setActiveTab = useCallback((key) => {
    const next = new URLSearchParams(searchParams);
    if (key === "analysis") next.delete("section");
    else next.set("section", key);
    if (key !== "transcript") next.delete("transcriptAt");
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const transcriptHrefFor = useCallback((timestamp) => {
    const value = String(timestamp || "").trim();
    const next = new URLSearchParams(searchParams);
    next.set("section", "transcript");
    if (value) next.set("transcriptAt", value);
    else next.delete("transcriptAt");
    next.delete("deepDiveTarget");
    const query = next.toString();
    return `${location.pathname}${query ? `?${query}` : ""}`;
  }, [location.pathname, searchParams]);
  const [activeStyle, setActiveStyle] = useState("first_person");
  const [crmNoteLanguage, setCrmNoteLanguage] = useState(
    () => localStorage.getItem("callsect_crm_note_language") || "english"
  );
  const [generating, setGenerating] = useState(false);
  const [rewriteAction, setRewriteAction] = useState(null);
  const [scoreUpdatingByStyle, setScoreUpdatingByStyle] = useState({});
  const [analyzing, setAnalyzing] = useState(false);
  const [repairingDeepDive, setRepairingDeepDive] = useState(false);
  const [findingsStep, setFindingsStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingMetadata, setEditingMetadata] = useState(false);
  const [savingMetadata, setSavingMetadata] = useState(false);
  const [metadataDraft, setMetadataDraft] = useState({ title: "", customerName: "" });
  const [metadataCustomerOptions, setMetadataCustomerOptions] = useState([]);
  const [transcriptTargetIndex, setTranscriptTargetIndex] = useState(null);
  const [highlightedTranscriptIndex, setHighlightedTranscriptIndex] = useState(null);
  const audioRef = useRef();
  const deepDiveTargetHandledRef = useRef(null);
  const noteActionLockRef = useRef(false);
  const noteScoreRevisionRef = useRef(0);
  const analysisActionLockRef = useRef(false);
  const repairActionLockRef = useRef(false);

  const load = async () => {
    const { data } = await api.get(`/calls/${id}`);
    setCall(data);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    setRelationshipContext(null);
    window.scrollTo({ top: 0, left: 0 });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const normalize = (value) => String(value || "").trim().toLocaleLowerCase();

    const loadRelationshipContext = async () => {
      if (!call?.id || (!call.customer_name && !call.relationship_id)) {
        if (!cancelled) setRelationshipContext(null);
        return;
      }

      try {
        const { data: customers } = await api.get("/customers");
        const directKey = call.relationship_id || call.relationship_key;
        const normalizedName = normalize(call.customer_name);
        const normalizedCompany = normalize(call.company);

        const matchedCustomer = (customers || []).find((customer) => (
          directKey
          && [customer.key, customer.id].some((value) => String(value || "") === String(directKey))
        )) || (customers || []).find((customer) => (
          normalize(customer.name) === normalizedName
          && (!normalizedCompany || !customer.company || normalize(customer.company) === normalizedCompany)
        )) || (directKey ? {
          key: directKey,
          id: directKey,
          name: call.customer_name,
          company: call.company,
        } : null);

        const relationshipKey = matchedCustomer?.key || matchedCustomer?.id;
        if (!relationshipKey) {
          if (!cancelled) setRelationshipContext(null);
          return;
        }

        const { data: timeline } = await api.get(
          `/customers/${encodeURIComponent(relationshipKey)}/timeline`
        );
        const chronologicalCalls = [...(timeline?.milestones || [])].sort(
          (left, right) => new Date(left.date).getTime() - new Date(right.date).getTime()
        );
        const currentIndex = chronologicalCalls.findIndex(
          (milestone) => String(milestone.call_id) === String(call.id)
        );

        if (!cancelled) {
          setRelationshipContext({
            key: relationshipKey,
            name: timeline?.customer?.name || matchedCustomer?.name || call.customer_name,
            company: timeline?.customer?.company || matchedCustomer?.company || call.company,
            previousCall: currentIndex > 0 ? chronologicalCalls[currentIndex - 1] : null,
            nextCall: currentIndex >= 0 && currentIndex < chronologicalCalls.length - 1
              ? chronologicalCalls[currentIndex + 1]
              : null,
          });
        }
      } catch {
        if (!cancelled) setRelationshipContext(null);
      }
    };

    loadRelationshipContext();
    return () => {
      cancelled = true;
    };
  }, [call?.company, call?.customer_name, call?.id, call?.relationship_id, call?.relationship_key]);

  useEffect(() => {
    localStorage.setItem("callsect_crm_note_language", crmNoteLanguage);
  }, [crmNoteLanguage]);

  useEffect(() => {
    if (!transcriptAt) return;
    const seconds = parseTimestampSeconds(transcriptAt);
    const segments = Array.isArray(call?.segments) ? call.segments : [];
    if (!segments.length || !Number.isFinite(seconds)) return;

    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    segments.forEach((segment, index) => {
      const start = Number(segment?.start);
      if (!Number.isFinite(start)) return;
      const distance = Math.abs(start - seconds);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    setTranscriptTargetIndex(nearestIndex);
  }, [call?.segments, transcriptAt]);

  useEffect(() => {
    if (activeTab !== "transcript" || transcriptTargetIndex == null) return undefined;

    const frame = window.requestAnimationFrame(() => {
      const target = document.querySelector(
        `[data-transcript-index="${transcriptTargetIndex}"]`
      );
      if (!target) return;

      target.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedTranscriptIndex(transcriptTargetIndex);
    });

    const clearHighlight = window.setTimeout(() => {
      setHighlightedTranscriptIndex(null);
      setTranscriptTargetIndex(null);
    }, 2600);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(clearHighlight);
    };
  }, [activeTab, transcriptTargetIndex]);

  useEffect(() => {
    const targetKey = deepDiveTarget;
    if (!targetKey || !call?.analysis) return undefined;

    const token = `${id}:${targetKey}`;
    if (deepDiveTargetHandledRef.current === token) return undefined;

    if (activeTab !== "analysis") {
      setActiveTab("analysis");
      return undefined;
    }

    let clearTarget = null;
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(`deep-dive-${targetKey}`)
        || document.getElementById("deep-dive");
      if (!target) return;

      target.scrollIntoView({ behavior: "smooth", block: "center" });
      target.classList.add("is-linked-target");
      deepDiveTargetHandledRef.current = token;
      clearTarget = window.setTimeout(() => {
        target.classList.remove("is-linked-target");
      }, 2600);
    });

    return () => {
      window.cancelAnimationFrame(frame);
      if (clearTarget) window.clearTimeout(clearTarget);
    };
  }, [activeTab, call?.analysis, deepDiveTarget, id, setActiveTab]);

  const runAnalysis = async () => {
    if (analysisActionLockRef.current) return;
    analysisActionLockRef.current = true;
    setAnalyzing(true);
    setFindingsStep(1);
    const findingsTimer = setTimeout(() => {
      setFindingsStep(2);
    }, 1500);
    try {
      const { data } = await api.post(`/calls/${id}/analyze`);
      setCall((c) => ({
        ...c,
        analysis: data.analysis,
        coaching: data.coaching,
        relationship_update: data.relationship_update || c.relationship_update,
      }));
      toast.success("Analysis complete");
    } catch {
      toast.error("Analysis failed");
    } finally {
      clearTimeout(findingsTimer);
      setAnalyzing(false);
      setFindingsStep(0);
      analysisActionLockRef.current = false;
    }
  };

  const generate = async (style) => {
    if (noteActionLockRef.current) return;
    noteActionLockRef.current = true;
    noteScoreRevisionRef.current += 1;
    setScoreUpdatingByStyle((current) => {
      if (!(style in current)) return current;
      const next = { ...current };
      delete next[style];
      return next;
    });
    setGenerating(true);
    setActiveStyle(style);
    try {
      const { data } = await api.post(`/calls/${id}/notes/generate`, {
        style,
        use_writing_profile: true,
        output_language: crmNoteLanguage,
      });
      setCall((c) => ({
        ...c,
        notes: {
          ...(c.notes || {}),
          [style]: {
            ...(c.notes?.[style] || {}),
            content: data.content,
            scores: data.scores,
            language_mode: data.language_mode,
            dna_alignment: data.dna_alignment,
          },
        },
      }));
      trackProductEvent("crm_note_generated", {
        section: "call_detail",
        outcome: style,
      });
      toast.success(`${style.replace(/_/g, " ")} note ready`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
      noteActionLockRef.current = false;
    }
  };

  const rewrite = async (instruction) => {
    const styleAtRequest = activeStyle;
    const languageAtRequest = crmNoteLanguage;
    const current = call?.notes?.[styleAtRequest]?.content;
    if (!current || noteActionLockRef.current) return;

    noteActionLockRef.current = true;
    const scoreRevision = noteScoreRevisionRef.current + 1;
    noteScoreRevisionRef.current = scoreRevision;
    setRewriteAction(instruction);

    let rewrittenText = null;
    let rewriteMetadata = null;
    try {
      const { data } = await api.post(`/calls/${id}/notes/rewrite`, {
        text: current,
        instruction,
        style: styleAtRequest,
        output_language: languageAtRequest,
      });
      rewrittenText = data.content;
      rewriteMetadata = data;
      setScoreUpdatingByStyle((current) => ({
        ...current,
        [styleAtRequest]: scoreRevision,
      }));
      setCall((c) => ({
        ...c,
        notes: {
          ...(c.notes || {}),
          [styleAtRequest]: {
            ...(c.notes?.[styleAtRequest] || {}),
            content: rewrittenText,
            language_mode: data.language_mode || languageAtRequest,
          },
        },
      }));
      toast.success("Rewritten");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Rewrite failed");
    } finally {
      // End the interactive wait as soon as the clean rewritten draft is ready.
      // The full Note Quality + Writing DNA alignment pass resumes separately
      // below, so it cannot hold the text UI hostage.
      setRewriteAction(null);
      noteActionLockRef.current = false;
    }

    if (!rewrittenText) return;

    // Resume the original Note Quality + Writing DNA alignment pipeline after
    // the clean rewritten draft is already visible. Do not await this request:
    // revision/content guards prevent a slower older pass from overwriting a
    // newer rewrite or a manual edit.
    void api.post(`/calls/${id}/notes/score`, {
      style: styleAtRequest,
      text: rewrittenText,
      postprocess: Boolean(rewriteMetadata?.postprocess_pending),
      rewrite_revision: rewriteMetadata?.rewrite_revision || null,
    }).then(({ data: qualityData }) => {
      if (noteScoreRevisionRef.current !== scoreRevision) return;
      setCall((c) => {
        if (c?.notes?.[styleAtRequest]?.content !== rewrittenText) return c;
        return {
          ...c,
          notes: {
            ...(c.notes || {}),
            [styleAtRequest]: {
              ...(c.notes?.[styleAtRequest] || {}),
              content: qualityData?.content || rewrittenText,
              scores: qualityData?.scores,
              dna_alignment: qualityData?.dna_alignment
                ?? c.notes?.[styleAtRequest]?.dna_alignment,
            },
          },
        };
      });
      setScoreUpdatingByStyle((current) => {
        if (current[styleAtRequest] !== scoreRevision) return current;
        const next = { ...current };
        delete next[styleAtRequest];
        return next;
      });
    }).catch((error) => {
      // The rewritten draft is already valid and saved. A background quality
      // pass failure must never turn a successful rewrite into a user-facing
      // failure or block the rewrite controls.
      console.warn("CRM note quality post-process failed after rewrite", error);
      setScoreUpdatingByStyle((current) => {
        if (current[styleAtRequest] !== scoreRevision) return current;
        const next = { ...current };
        delete next[styleAtRequest];
        return next;
      });
    });
  };


  const repairDeepDive = async () => {
    if (repairActionLockRef.current || repairingDeepDive) return;
    repairActionLockRef.current = true;
    setRepairingDeepDive(true);
    try {
      const { data } = await api.post(`/calls/${id}/analysis/repair-deep-dive`);
      setCall((current) => ({
        ...current,
        analysis: data.analysis || current.analysis,
        relationship_update: data.relationship_update || current.relationship_update,
      }));
      toast.success(data.already_healthy ? "Deep Dive is already healthy" : "Deep Dive repaired");
      if (data.relationship_rebuild_error) {
        toast.warning(`Deep Dive repaired. Relationship memory rebuild pending: ${data.relationship_rebuild_error}`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Deep Dive repair failed");
    } finally {
      setRepairingDeepDive(false);
      repairActionLockRef.current = false;
    }
  };

  const copy = () => {
    const c = call?.notes?.[activeStyle]?.content;
    if (!c) return;
    navigator.clipboard.writeText(c);
    toast.success("Copied to clipboard");
  };

  const download = (format) => {
    const c = call?.notes?.[activeStyle]?.content;
    if (!c) return;
    const text = format === "md" ? `# ${call.title}\n\n${c}` : c;
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${call.title || "note"}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteCall = async () => {
    setDeleting(true);
    try {
      await api.delete(`/calls/${id}`);
      toast.success("Call deleted");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      const message = error?.response?.data?.detail || "The call could not be deleted";
      toast.error(message);
      setDeleting(false);
    }
  };

  const beginMetadataEdit = () => {
    setMetadataDraft({
      title: call?.title || "",
      customerName: call?.customer_name || "",
    });
    setEditingMetadata(true);
    if (!metadataCustomerOptions.length) {
      api.get("/customers")
        .then(({ data }) => {
          const names = Array.from(new Set(
            (Array.isArray(data) ? data : [])
              .map((customer) => String(customer?.name || "").trim())
              .filter(Boolean),
          ));
          setMetadataCustomerOptions(names);
        })
        .catch(() => {
          // Suggestions are optional; editing remains fully available.
        });
    }
  };

  const cancelMetadataEdit = () => {
    if (savingMetadata) return;
    setEditingMetadata(false);
    setMetadataDraft({ title: "", customerName: "" });
  };

  const saveMetadataEdit = async () => {
    if (savingMetadata || !call) return;
    const nextTitle = String(metadataDraft.title || "").trim().replace(/\s+/g, " ");
    const nextCustomer = String(metadataDraft.customerName || "").trim().replace(/\s+/g, " ");
    if (!nextTitle) {
      toast.error("Call name cannot be empty.");
      return;
    }
    if (!nextCustomer) {
      toast.error("Customer name cannot be empty.");
      return;
    }

    const titleChanged = nextTitle !== String(call.title || "").trim().replace(/\s+/g, " ");
    const customerDisplayChanged = nextCustomer !== String(call.customer_name || "").trim().replace(/\s+/g, " ");
    const customerRelationshipChanged = normalizedMetadataValue(nextCustomer) !== normalizedMetadataValue(call.customer_name);
    if (!titleChanged && !customerDisplayChanged) {
      setEditingMetadata(false);
      return;
    }

    setSavingMetadata(true);
    try {
      const payload = {};
      if (titleChanged) payload.title = nextTitle;
      if (customerDisplayChanged) payload.customer_name = nextCustomer;
      const { data } = await api.patch(`/calls/${id}/metadata`, payload);
      const updatedCall = data?.call || {
        ...call,
        ...(titleChanged ? { title: nextTitle } : {}),
        ...(customerDisplayChanged ? { customer_name: nextCustomer } : {}),
      };
      setCall(updatedCall);
      if (customerDisplayChanged) setRelationshipContext(null);
      setEditingMetadata(false);
      setMetadataDraft({ title: "", customerName: "" });
      if (data?.customer_reassigned || customerRelationshipChanged) {
        toast.success("Call updated. Relationship context is refreshing.");
      } else if (customerDisplayChanged) {
        toast.success("Customer name updated.");
      } else {
        toast.success("Call name updated.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "The call details could not be updated.");
    } finally {
      setSavingMetadata(false);
    }
  };

  if (loading) return (
    <div className="call-detail-page">
      <Nav />
      <main className="call-detail-shell">
        <div className="call-detail-loading" role="status" aria-live="polite">
          <span />
          <span />
          <span />
          <span className="sr-only">Loading call...</span>
        </div>
      </main>
    </div>
  );
  if (!call) return (
    <div className="call-detail-page">
      <Nav />
      <main className="call-detail-shell">
        <div className="call-detail-not-found">
          <PhoneCall size={28} weight="duotone" />
          <strong>Call not found.</strong>
          <button type="button" onClick={() => navigate("/dashboard")} className="call-detail-secondary-button">Back to calls</button>
        </div>
      </main>
    </div>
  );

  const currentNote = call.notes?.[activeStyle]?.content;
  const analysis = call.analysis;
  const coaching = call.coaching;
  const relationshipKey = relationshipContext?.key || call.relationship_id || call.relationship_key || location.state?.relationshipKey;
  const relationshipName = relationshipContext?.name || call.customer_name || location.state?.relationshipName;
  const relationshipPath = relationshipKey
    ? `/customers/${encodeURIComponent(relationshipKey)}`
    : null;
  const cameFromRelationship = location.state?.from === "relationship" && relationshipPath;
  const relationshipReturnState = relationshipPath
    ? { activeCallId: call.id }
    : undefined;

  return (
    <div className="call-detail-page">
      <Nav />
      <main className="call-detail-shell">
        {!cameFromRelationship && (
          <div className="call-detail-context-topbar">
            <Link
              to="/dashboard"
              className="call-detail-back-link"
            >
              <ArrowLeft size={15} weight="bold" />
              Back to calls
            </Link>
          </div>
        )}

        <header className="call-detail-header">
          <div className="call-detail-header__identity">
            <span className="call-detail-header__mark" aria-hidden="true">
              <PhoneCall size={24} weight="duotone" />
            </span>
            <div className="call-detail-header__copy">
              {editingMetadata ? (
                <div className="call-detail-metadata-editor">
                  <label>
                    <span>Call name</span>
                    <input
                      type="text"
                      value={metadataDraft.title}
                      onChange={(event) => setMetadataDraft((current) => ({ ...current, title: event.target.value }))}
                      maxLength={180}
                      autoFocus
                      disabled={savingMetadata}
                      aria-label="Call name"
                    />
                  </label>
                  <label>
                    <span>Customer</span>
                    <input
                      type="text"
                      value={metadataDraft.customerName}
                      onChange={(event) => setMetadataDraft((current) => ({ ...current, customerName: event.target.value }))}
                      list="call-detail-customer-options"
                      maxLength={180}
                      disabled={savingMetadata}
                      aria-label="Customer name"
                    />
                  </label>
                  <datalist id="call-detail-customer-options">
                    {metadataCustomerOptions.map((name) => <option key={name} value={name} />)}
                  </datalist>
                  {normalizedMetadataValue(metadataDraft.customerName) !== normalizedMetadataValue(call.customer_name) && (
                    <p className="call-detail-metadata-editor__warning">
                      Changing the customer will move this call to another relationship and refresh both histories.
                    </p>
                  )}
                </div>
              ) : (
                <h1>{call.title}</h1>
              )}
              <div className="call-detail-meta">
                {!editingMetadata && call.customer_name && (
                  relationshipPath ? (
                    <Link
                      to={relationshipPath}
                      state={relationshipReturnState}
                      className="call-detail-meta__relationship"
                    >
                      {call.customer_name}
                    </Link>
                  ) : (
                    <span>{call.customer_name}</span>
                  )
                )}
                {!editingMetadata && call.company && <span>{call.company}</span>}
                <span>{fmtDateTime(call.occurred_at || call.created_at)}</span>
                <span>{fmt(call.duration)}</span>
              </div>
            </div>
          </div>

          <div className="call-detail-actions">
            {editingMetadata ? (
              <>
                <button
                  type="button"
                  onClick={cancelMetadataEdit}
                  disabled={savingMetadata}
                  className="call-detail-secondary-button"
                >
                  <X size={15} weight="bold" />
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveMetadataEdit}
                  disabled={savingMetadata}
                  className="call-detail-primary-button"
                >
                  {savingMetadata ? <WorkingHourglass size={15} /> : <Check size={15} weight="bold" />}
                  {savingMetadata ? "Saving..." : "Save changes"}
                </button>
              </>
            ) : (
              <>
                {!analysis && (
                  <button
                    onClick={runAnalysis}
                    disabled={analyzing}
                    data-testid="run-analysis-btn"
                    className="call-detail-primary-button"
                  >
                    {analyzing ? <WorkingHourglass size={16} /> : <Target size={16} weight="fill" />} {analyzing ? "Analyzing..." : "Analyze call"}
                  </button>
                )}

                <button
                  type="button"
                  onClick={beginMetadataEdit}
                  className="call-detail-secondary-button call-detail-metadata-edit-button"
                  data-testid="edit-call-metadata"
                >
                  <PencilSimple size={15} weight="bold" />
                  Edit details
                </button>

                <button
                  type="button"
                  onClick={() => setDeleteDialogOpen(true)}
                  data-testid="open-delete-call-dialog"
                  className="call-detail-danger-button call-detail-danger-button--header"
                >
                  <Trash size={16} weight="fill" />
                  Delete call
                </button>
              </>
            )}
          </div>
        </header>

        {relationshipPath && shouldShowConversationNavigation(relationshipContext) && (
          <nav className="call-context-navigation" aria-label="Conversation navigation">
            <div className="call-context-navigation__sequence">
              {relationshipContext?.previousCall ? (
                <Link
                  to={`/calls/${relationshipContext.previousCall.call_id}`}
                  state={{
                    from: "relationship",
                    relationshipKey,
                    relationshipName,
                  }}
                  className="call-context-navigation__call"
                  aria-label={`Open previous call: ${relationshipContext.previousCall.title || "Previous call"}`}
                >
                  <ArrowLeft size={19} weight="bold" aria-hidden="true" />
                  <span>
                    <small>Previous call</small>
                    <strong>{relationshipContext.previousCall.title || "Earlier conversation"}</strong>
                    {relationshipContext.previousCall.date && (
                      <em>{fmtDateTime(relationshipContext.previousCall.date)}</em>
                    )}
                  </span>
                </Link>
              ) : (
                <span className="call-context-navigation__call is-disabled">
                  <ArrowLeft size={19} weight="bold" aria-hidden="true" />
                  <span>
                    <small>Previous call</small>
                    <strong>No earlier conversation</strong>
                  </span>
                </span>
              )}

              {relationshipContext?.nextCall ? (
                <Link
                  to={`/calls/${relationshipContext.nextCall.call_id}`}
                  state={{
                    from: "relationship",
                    relationshipKey,
                    relationshipName,
                  }}
                  className="call-context-navigation__call is-next"
                  aria-label={`Open next call: ${relationshipContext.nextCall.title || "Next call"}`}
                >
                  <span>
                    <small>Next call</small>
                    <strong>{relationshipContext.nextCall.title || "Later conversation"}</strong>
                    {relationshipContext.nextCall.date && (
                      <em>{fmtDateTime(relationshipContext.nextCall.date)}</em>
                    )}
                  </span>
                  <ArrowRight size={19} weight="bold" aria-hidden="true" />
                </Link>
              ) : (
                <span className="call-context-navigation__call is-next is-disabled">
                  <span>
                    <small>Next call</small>
                    <strong>No later conversation</strong>
                  </span>
                  <ArrowRight size={19} weight="bold" aria-hidden="true" />
                </span>
              )}
            </div>
          </nav>
        )}

        {deleteDialogOpen && (
          <div className="call-detail-dialog-backdrop" role="dialog" aria-modal="true" aria-labelledby="delete-call-title" data-testid="delete-call-dialog">
            <div className="call-detail-dialog">
              <div className="call-detail-dialog__header">
                <div>
                  <div className="call-detail-dialog__danger"><Trash size={20} weight="fill" /></div>
                  <h2 id="delete-call-title">Delete this call?</h2>
                </div>
                <button type="button" onClick={() => setDeleteDialogOpen(false)} disabled={deleting} aria-label="Close" className="call-detail-dialog__close">
                  <X size={18} />
                </button>
              </div>
              <p><strong>{call.title}</strong>{call.customer_name ? ` for ${call.customer_name}` : ""} will be permanently removed, including its transcript, analysis, coaching and CRM notes.</p>
              <small>This action cannot be undone.</small>
              <div className="call-detail-dialog__actions">
                <button type="button" onClick={() => setDeleteDialogOpen(false)} disabled={deleting} className="call-detail-secondary-button">Cancel</button>
                <button type="button" onClick={deleteCall} disabled={deleting} data-testid="confirm-delete-call" className="call-detail-danger-button">
                  <Trash size={15} weight="fill" /> {deleting ? "Deleting..." : "Delete call"}
                </button>
              </div>
            </div>
          </div>
        )}

        <LongitudinalDelta
          update={call.relationship_update}
          analysis={call.analysis}
          relationshipPath={relationshipPath}
          relationshipName={relationshipName}
          relationshipReturnState={relationshipReturnState}
        />

        <nav className="call-detail-tabs" aria-label="Call detail sections" role="tablist">
          {[
            { key: "analysis", label: "Dissection", icon: MagnifyingGlass },
            { key: "coaching", label: "Coaching", icon: ChartBar },
            { key: "notes", label: "CRM Notes", icon: ClipboardText },
            { key: "transcript", label: "Transcript", icon: ChatCircle },
          ].map((tab) => (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`call-detail-tab${activeTab === tab.key ? " is-active" : ""}`}
              role="tab"
              id={`call-tab-${tab.key}`}
              aria-controls={`call-panel-${tab.key}`}
              aria-selected={activeTab === tab.key}
              tabIndex={activeTab === tab.key ? 0 : -1}
            >
              <tab.icon size={16} weight={activeTab === tab.key ? "fill" : "regular"} />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div
          className="call-detail-tab-content"
          key={activeTab}
          role="tabpanel"
          id={`call-panel-${activeTab}`}
          aria-labelledby={`call-tab-${activeTab}`}
        >
          {activeTab === "notes" && (
            <section>
              <div className="call-detail-section-intro">
                <div>
                  <div className="brand-eyebrow">Your voice, structured</div>
                  <h2>Generate notes in your own writing style.</h2>
                  <p>Choose a format, select the output language, then refine the note without losing your reasoning or tone.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[minmax(9rem,0.62fr)_minmax(0,3.45fr)]">
                <aside className="call-detail-style-panel">
                  <div className="call-detail-panel-label">Styles</div>
                  {STYLES.map((style) => {
                    const hasNote = Boolean(call.notes?.[style.key]);
                    return (
                      <button
                        key={style.key}
                        data-testid={`style-${style.key}`}
                        onClick={() => setActiveStyle(style.key)}
                        className={`call-detail-style-button${activeStyle === style.key ? " is-active" : ""}`}
                      >
                        <span>{style.label}</span>
                        {hasNote && <span className="call-detail-style-button__ready" />}
                      </button>
                    );
                  })}
                </aside>

                <section className="call-detail-note-editor">
                  <div className="call-detail-toolbar">
                    <label className="call-detail-language">
                      <span>CRM note language</span>
                      <select value={crmNoteLanguage} onChange={(event) => setCrmNoteLanguage(event.target.value)} data-testid="crm-note-language">
                        <option value="english">English (default)</option>
                        <option value="conversation">Conversation language</option>
                      </select>
                    </label>

                    <button data-testid="generate-note-btn" onClick={() => generate(activeStyle)} disabled={generating || Boolean(rewriteAction)} className="call-detail-primary-button call-detail-primary-button--compact">
                      {generating ? <WorkingHourglass size={14} /> : <Sparkle size={14} weight="fill" />}
                      {generating ? "Writing..." : currentNote ? "Regenerate" : "Generate"}
                    </button>

                    {currentNote && (
                      <>
                        <RewriteBtn onClick={() => rewrite("shorter")} label="Shorter" disabled={generating || Boolean(rewriteAction)} busy={rewriteAction === "shorter"} />
                        <RewriteBtn onClick={() => rewrite("more_professional")} label="More professional" disabled={generating || Boolean(rewriteAction)} busy={rewriteAction === "more_professional"} />
                        <RewriteBtn onClick={() => rewrite("more_conversational")} label="More conversational" disabled={generating || Boolean(rewriteAction)} busy={rewriteAction === "more_conversational"} />
                        <RewriteBtn onClick={() => rewrite("more_detailed")} label="More detailed" disabled={generating || Boolean(rewriteAction)} busy={rewriteAction === "more_detailed"} />
                        <div className="call-detail-toolbar__exports">
                          <button onClick={copy} data-testid="copy-btn" className="call-detail-secondary-button"><Copy size={14} /> Copy</button>
                          <button onClick={() => download("md")} data-testid="download-md-btn" className="call-detail-secondary-button">.md</button>
                          <button onClick={() => download("txt")} data-testid="download-txt-btn" className="call-detail-secondary-button">.txt</button>
                        </div>
                      </>
                    )}
                  </div>

                  {currentNote ? (
                    <>
                      <textarea
                        data-testid="note-textarea"
                        value={currentNote}
                        readOnly
                        aria-readonly="true"
                        spellCheck={false}
                        className="call-detail-note-textarea call-detail-note-textarea--readonly"
                      />
                      <QualityScorePanel
                        scores={call.notes?.[activeStyle]?.scores}
                        updating={Boolean(scoreUpdatingByStyle[activeStyle])}
                      />
                    </>
                  ) : (
                    <div className="call-detail-empty-state">
                      <RevealLinesMark />
                      <strong>Ready when you are.</strong>
                      <p>Generate this format and Callsect will write it using your Writing DNA.</p>
                    </div>
                  )}
                </section>

              </div>
            </section>
          )}

          {activeTab === "transcript" && (
            <section className="call-detail-transcript">
              <div className="call-detail-transcript__header">
                <div>
                  <div className="brand-eyebrow">Conversation record</div>
                  <h2>Transcript</h2>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {call.transcription?.timestamps_estimated && (
                    <span className="rounded-full border border-amber-400/20 bg-amber-400/[0.06] px-2.5 py-1 text-[11px] text-amber-200">Approximate positions</span>
                  )}
                  <span>{(call.segments || []).length ? `${call.segments.length} segments` : "Full text"}</span>
                </div>
              </div>
              {call.filename && (
                <audio ref={audioRef} onTimeUpdate={(event) => setCurrentTime(event.target.currentTime)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} className="hidden" />
              )}
              <div className="call-detail-transcript__body intel-scrollbar">
                {(call.segments || []).length === 0 ? (
                  <div className="call-detail-transcript__plain">{formatPlainTranscriptSpeakers(call.transcript)}</div>
                ) : (
                  call.segments.map((segment, index) => {
                    const speaker = transcriptSpeakerPresentation(segment.speaker);
                    return (
                      <div
                        key={index}
                        data-transcript-index={index}
                        className={`transcript-row${highlightedTranscriptIndex === index ? " is-highlighted" : ""}`}
                      >
                        <div className="transcript-time">{fmt(segment.start)}</div>
                        <div className="transcript-copy">
                          <span className={`transcript-speaker ${speaker.toneClass}`.trim()}>{speaker.label}</span>
                          <span>{segment.text}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          )}

          {activeTab === "analysis" && (
            <AnalysisView
              analysis={analysis}
              onRun={runAnalysis}
              loading={analyzing}
              findingsStep={findingsStep}
              relationshipPath={relationshipPath}
              relationshipName={relationshipName}
              relationshipReturnState={relationshipReturnState}
              timestampHrefBuilder={transcriptHrefFor}
              deepDiveTarget={deepDiveTarget}
              onRepairDeepDive={repairDeepDive}
              repairingDeepDive={repairingDeepDive}
              processingStatus={call.processing?.status}
            />
          )}

          {activeTab === "coaching" && (
            <CoachingView
              coaching={coaching}
              onRun={runAnalysis}
              loading={analyzing}
              processingStatus={call.processing?.status}
              timestampHrefBuilder={transcriptHrefFor}
              transcriptSegments={call.segments}
            />
          )}
        </div>
      </main>
    </div>
  );
}

function RewriteBtn({ onClick, label, disabled = false, busy = false }) {
  return (
    <button disabled={disabled} onClick={onClick} data-testid={`rewrite-${label.toLowerCase().replace(/\s+/g, "-")}`} className="call-detail-secondary-button">
      {busy ? <WorkingHourglass size={12} /> : <ArrowClockwise size={12} />} {busy ? "Rewriting..." : label}
    </button>
  );
}

const SIGNAL_DESCRIPTIONS = {
  buyingIntent: "How strongly the conversation suggests a willingness to buy, renew, or move forward.",
  trust: "How much confidence the customer shows in the rep, company, and proposed solution.",
  relationship: "The overall strength and stability of the customer relationship.",
  confidence: "How certain and decisive the customer appears about their position.",
  churnRisk: "The likelihood that the customer may downgrade, cancel, or not renew.",
  quality: "The overall commercial quality and effectiveness of the conversation.",
};

const SKILL_DESCRIPTIONS = {
  empathy: "How well the rep acknowledges the customer's perspective and emotions.",
  listening: "How effectively the rep understands and responds to what the customer actually says.",
  conversationControl: "How well the rep guides the discussion toward a useful outcome without dominating it.",
};

function scoreTone(value, invert = false) {
  return scoreToTone(value, { invert });
}

function Bar({ label, value, description, reason, directionNote, invert = false }) {
  const hasScore = isNumericScore(value);
  const normalized = hasScore ? Math.max(0, Math.min(100, Number(value))) : null;
  const tone = hasScore ? scoreTone(normalized, invert) : "";

  return (
    <div className={`findings-score${tone ? ` findings-score--${tone}` : ""}`}>
      <div className="findings-score__head">
        <div className="findings-score__label">
          <span className="findings-score__label-text">{label}</span>
        </div>
        <ScoreInsight label={label} reason={reason} description={description} directionNote={directionNote} />
      </div>
      <div className="score-meter">
        <div
          className={`score-meter__fill${tone ? ` score-meter__fill--${tone}` : ""}`}
          style={{ "--score-meter-scale": hasScore ? normalized / 100 : 0 }}
        />
      </div>
    </div>
  );
}

function renderWithItalicQuotes(value, quoteClassName) {
  const text = String(value ?? "");
  const parts = text.split(/(“[^”]*”|"[^"]*")/g);
  return parts.map((part, index) => {
    const isQuote = /^(“[^”]*”|"[^"]*")$/.test(part);
    return isQuote ? <em key={`${index}-${part}`} className={quoteClassName || undefined}>{part}</em> : part;
  });
}

function splitLeadingEvidenceQuote(value) {
  const text = String(value ?? "").trim();
  const match = text.match(/^(“[^”]*”|"[^"]*")\s*(?:—|–|-)\s*(.*)$/s);
  if (!match) return { quote: "", commentary: text };
  return { quote: match[1], commentary: String(match[2] || "").trim() };
}

function splitQuestionGuidance(value) {
  const text = String(value ?? "").trim();
  if (!text) return { evidenceQuote: "", lead: "", suggestion: "" };

  // Coaching may optionally start with transcript evidence followed by an em dash.
  // Keep that quote visually distinct from the future wording Callsect suggests.
  let evidenceQuote = "";
  let guidance = text;
  const evidence = splitLeadingEvidenceQuote(text);
  if (evidence.quote) {
    evidenceQuote = evidence.quote;
    guidance = evidence.commentary;
  }

  // Framing labels are intentionally language-agnostic: any concise leading clause ending
  // in a colon becomes its own line (e.g. "In un prossimo confronto:").
  const labelMatch = String(guidance || "").match(/^([^:\n]{1,120}:)\s*(.*)$/s);
  if (labelMatch) {
    return {
      evidenceQuote,
      lead: String(labelMatch[1] || "").trim(),
      suggestion: String(labelMatch[2] || "").trim(),
    };
  }

  // Legacy items may contain only a framing label plus a quoted suggested question.
  // They still belong to guidance, not transcript evidence, unless an evidence dash was found above.
  return { evidenceQuote, lead: "", suggestion: String(guidance || "").trim() };
}

function formatSuggestedQuestion(value) {
  let text = String(value ?? "").trim();
  if (!text) return "";

  // Suggested future wording is guidance, not transcript evidence. Normalize only
  // its presentation so legacy/new outputs share the same visual grammar.
  text = text.replace(/^[\s“”"'‘’]+|[\s“”"'‘’]+$/g, "").trim();
  if (!text) return "";

  const chars = Array.from(text);
  const firstLetterIndex = chars.findIndex((char) => /\p{L}/u.test(char));
  if (firstLetterIndex >= 0) {
    chars[firstLetterIndex] = chars[firstLetterIndex].toLocaleUpperCase();
  }

  return `“${chars.join("")}”`;
}

function CoachingQuestionPrompt({ value, confidence, testId }) {
  const { evidenceQuote, lead, suggestion } = splitQuestionGuidance(value);
  const formattedSuggestion = formatSuggestedQuestion(suggestion);

  return (
    <span className="coaching-question-prompt">
      {evidenceQuote && (
        <span className="coaching-question-prompt__evidence">
          <em className="evidence-verbatim-quote">{evidenceQuote}</em>
          {!lead && !suggestion && <ConfChip confidence={confidence} testId={testId} inline />}
        </span>
      )}
      {lead && <span className="coaching-question-prompt__lead">{lead}</span>}
      {formattedSuggestion ? (
        <span className="coaching-question-prompt__suggestion">
          <em>{formattedSuggestion}</em>
          <ConfChip confidence={confidence} testId={testId} inline />
        </span>
      ) : (!evidenceQuote && lead ? (
        <span className="coaching-question-prompt__lead-only">
          <ConfChip confidence={confidence} testId={testId} inline />
        </span>
      ) : null)}
    </span>
  );
}

function CoachingEvidenceCopy({ value, confidence, testId }) {
  const { quote, commentary } = splitLeadingEvidenceQuote(value);
  if (!quote) {
    return (
      <span className="coaching-evidence__copy">
        <span className="coaching-evidence__commentary">{value}<ConfChip confidence={confidence} testId={testId} inline /></span>
      </span>
    );
  }

  return (
    <span className="coaching-evidence__copy">
      <span className="coaching-evidence__quote-line">
        <em className="evidence-verbatim-quote coaching-evidence__quote">{quote}</em>
        {!commentary && <ConfChip confidence={confidence} testId={testId} inline />}
      </span>
      {commentary && (
        <span className="coaching-evidence__commentary">{renderWithItalicQuotes(commentary)}<ConfChip confidence={confidence} testId={testId} inline /></span>
      )}
    </span>
  );
}

function CoachingTimestamp({ value, href }) {
  const timestamp = String(value || "").trim();
  if (!timestamp) return null;
  const seconds = parseTimestampSeconds(timestamp);
  if (href && Number.isFinite(seconds)) {
    return (
      <Link
        to={href}
        className="coaching-evidence__timestamp"
        aria-label={`Open transcript at ${timestamp}`}
        title="Open this moment in the transcript"
      >
        {timestamp}
      </Link>
    );
  }
  return <span className="coaching-evidence__timestamp is-static">{timestamp}</span>;
}

function CoachingSpeaker({ speaker }) {
  if (!speaker?.label || !speaker?.toneClass) return null;
  return (
    <span className={`transcript-speaker coaching-evidence__speaker ${speaker.toneClass}`.trim()}>
      {speaker.label}
    </span>
  );
}

function CoachingEvidenceMeta({ speaker, timestamp, href, placeholder = false }) {
  const hasTimestamp = Boolean(String(timestamp || "").trim());
  if (!hasTimestamp && !placeholder) return null;
  return (
    <span className={`coaching-evidence__meta${hasTimestamp ? "" : " is-placeholder"}`}>
      {hasTimestamp && <CoachingSpeaker speaker={speaker} />}
      {hasTimestamp ? (
        <CoachingTimestamp value={timestamp} href={href} />
      ) : (
        <span className="coaching-evidence__timestamp coaching-evidence__timestamp--placeholder" aria-hidden="true" />
      )}
    </span>
  );
}

function List({ title, items, icon: Icon, color = "text-slate-300", testIdPrefix, italicQuotes = false, showTimestamps = false, timestampHrefBuilder, speakerResolver, questionPrompt = false }) {
  if (!Array.isArray(items) || items.length === 0) return null;

  // Questions to Ask can mix evidence-grounded prompts with forward-looking prompts that
  // do not need transcript evidence. Keep grounded items first, then place no-timestamp
  // items last while preserving the model's original order within each group.
  const displayItems = items
    .map((item, originalIndex) => ({
      item,
      originalIndex,
      hasTimestamp: Boolean(String(item?.timestamp || "").trim()),
    }))
    .sort((a, b) => {
      if (!questionPrompt || a.hasTimestamp === b.hasTimestamp) return a.originalIndex - b.originalIndex;
      return a.hasTimestamp ? -1 : 1;
    });

  return (
    <div className="findings-list">
      <div className="findings-list__title">{Icon && <Icon size={13} weight="fill" />} {title}</div>
      <ul>
        {displayItems.map(({ item, originalIndex, hasTimestamp }, index) => {
          const { text, confidence } = itemParts(item);
          const speaker = hasTimestamp && typeof speakerResolver === "function"
            ? speakerResolver(item)
            : null;
          const evidenceLineClass = (showTimestamps || italicQuotes)
            ? `coaching-evidence__line${showTimestamps && (hasTimestamp || questionPrompt) ? " coaching-evidence__line--with-meta" : ""}${questionPrompt ? " coaching-evidence__line--question" : ""}`
            : undefined;
          return (
            <li key={originalIndex} className={color} data-testid={testIdPrefix ? `${testIdPrefix}-${index}` : undefined}>
              <span className="findings-list__bullet" />
              <span className={evidenceLineClass}>
                {showTimestamps && (hasTimestamp || questionPrompt) && (
                  <CoachingEvidenceMeta
                    speaker={speaker}
                    timestamp={hasTimestamp ? item?.timestamp : null}
                    href={hasTimestamp && typeof timestampHrefBuilder === "function" ? timestampHrefBuilder(item?.timestamp) : null}
                    placeholder={questionPrompt && !hasTimestamp}
                  />
                )}
                {questionPrompt ? (
                  <CoachingQuestionPrompt
                    value={text}
                    confidence={confidence}
                    testId={testIdPrefix ? `${testIdPrefix}-${index}-conf` : undefined}
                  />
                ) : italicQuotes ? (
                  <CoachingEvidenceCopy
                    value={text}
                    confidence={confidence}
                    testId={testIdPrefix ? `${testIdPrefix}-${index}-conf` : undefined}
                  />
                ) : (
                  <span>{text}<ConfChip confidence={confidence} testId={testIdPrefix ? `${testIdPrefix}-${index}-conf` : undefined} inline /></span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function CompactSignal({ label, value, invert = false }) {
  const numeric = Math.max(0, Math.min(100, Number(value) || 0));
  const tone = scoreToTone(numeric, { invert });
  return (
    <div className={`findings-signal-pill findings-signal-pill--${tone}`}>
      <span>{label}</span>
      <strong className="score-grade">{scoreToRating(numeric)}</strong>
    </div>
  );
}

function FindingGroup({ title, items, icon: Icon, tone = "violet", emptyText }) {
  const visible = Array.isArray(items) ? items.filter((item) => itemParts(item).text) : [];
  if (!visible.length && !emptyText) return null;
  return (
    <div className={`findings-classified-group findings-classified-group--${tone}`}>
      <div className="findings-classified-group__title">
        {Icon && <Icon size={14} weight="fill" aria-hidden="true" />}
        {title}
      </div>
      {visible.length ? (
        <ul>
          {visible.map((item, index) => {
            const { text, confidence } = itemParts(item);
            return (
              <li key={index}>
                <span className="findings-list__bullet" />
                <span>{text}<ConfChip confidence={confidence} inline /></span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="findings-classified-group__empty">{emptyText}</p>
      )}
    </div>
  );
}

function ActionList({ actions }) {
  const visible = Array.isArray(actions) ? actions.filter((item) => itemParts(item).text) : [];
  if (!visible.length) return null;
  const ownerLabel = {
    customer: "Customer",
    sales_rep: "Rep",
    other: "Other",
    unknown: "Owner unclear",
  };
  return (
    <ul className="findings-action-list">
      {visible.map((action, index) => {
        const text = action?.text || action?.what || itemParts(action).text;
        const owner = action?.owner || action?.who || "unknown";
        const deadline = action?.deadline;
        return (
          <li key={index}>
            <span className={`findings-owner-chip findings-owner-chip--${owner}`}>
              {ownerLabel[owner] || ownerLabel.unknown}
            </span>
            <span className="findings-action-list__copy">
              <strong>{localizeSellerRole(text)}</strong>
              {deadline && String(deadline).toLowerCase() !== "not established" && (
                <small>{deadline}</small>
              )}
            </span>
            <ConfChip confidence={action?.confidence} />
          </li>
        );
      })}
    </ul>
  );
}

function parseTimestampSeconds(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const match = raw.match(/^(?:(\d{1,2}):)?(\d{1,2}):(\d{2})$/);
  if (!match) return null;

  const hours = Number(match[1] || 0);
  const minutes = Number(match[2] || 0);
  const seconds = Number(match[3] || 0);
  if (minutes > 59 || seconds > 59) return null;

  return (hours * 3600) + (minutes * 60) + seconds;
}

function coachingSpeakerPresentation(rawSpeaker) {
  if (!rawSpeaker) return null;
  const presentation = transcriptSpeakerPresentation(rawSpeaker);
  return presentation.role === "rep" || presentation.role === "client" ? presentation : null;
}

function coachingSpeakerForEvidence(segments, evidence) {
  const explicitSpeaker = coachingSpeakerPresentation(evidence?.evidence_speaker);
  if (explicitSpeaker) return explicitSpeaker;

  const seconds = parseTimestampSeconds(evidence?.timestamp);
  if (!Number.isFinite(seconds) || !Array.isArray(segments)) return null;

  const candidates = segments.filter((segment) => (
    segment
    && String(segment.text || "").trim()
    && Math.floor(Number(segment.start || 0)) === seconds
  ));
  if (!candidates.length) return null;

  const sourceText = evidence?.text ?? evidence?.situation ?? "";
  const { quote } = splitLeadingEvidenceQuote(sourceText);
  const normalizedQuote = normalizeEvidenceText(quote);
  if (normalizedQuote) {
    const matches = candidates.filter((segment) => (
      normalizeEvidenceText(segment.text).includes(normalizedQuote)
    ));
    if (matches.length === 1) return coachingSpeakerPresentation(matches[0].speaker);
    if (matches.length > 1) return null;
  }

  return candidates.length === 1 ? coachingSpeakerPresentation(candidates[0].speaker) : null;
}

function normalizeEvidenceText(value) {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function evidenceDescriptionIsRedundant(description, findingText, quote) {
  const descriptionText = normalizeEvidenceText(description);
  if (!descriptionText) return true;

  const finding = normalizeEvidenceText(findingText);
  const evidenceQuote = normalizeEvidenceText(quote);
  if ((finding && (descriptionText === finding || finding.includes(descriptionText) || descriptionText.includes(finding)))
    || (evidenceQuote && (descriptionText === evidenceQuote || evidenceQuote.includes(descriptionText)))) {
    return true;
  }

  const descriptionTokens = new Set(descriptionText.split(" ").filter((token) => token.length >= 4));
  const findingTokens = new Set(finding.split(" ").filter((token) => token.length >= 4));
  if (!descriptionTokens.size || !findingTokens.size) return false;
  const shared = [...descriptionTokens].filter((token) => findingTokens.has(token)).length;
  return shared / Math.min(descriptionTokens.size, findingTokens.size) >= 0.78;
}

function KeyMomentList({ items, timestampHrefBuilder, findingText = "" }) {
  const visible = Array.isArray(items)
    ? items.filter((item) => {
        const timestamp = item?.timestamp || item?.timestamp_estimate;
        const quote = String(item?.quote || "").trim();
        const description = localizeSellerRole(String(item?.description || "").trim());
        return timestamp
          && quote
          && quote.toLowerCase() !== "no direct quote"
          && !evidenceDescriptionIsRedundant(description, findingText, quote);
      }).slice(0, 6)
    : [];
  if (!visible.length) return null;

  return (
    <div className="findings-key-moments findings-key-moments--evidence-first">
      {visible.map((item, index) => {
        const timestamp = item?.timestamp || item?.timestamp_estimate || `0${index + 1}`;
        const seconds = parseTimestampSeconds(timestamp);
        const timestampHref = Number.isFinite(seconds) && typeof timestampHrefBuilder === "function"
          ? timestampHrefBuilder(timestamp)
          : null;
        const quote = String(item?.quote || "").trim();
        const description = localizeSellerRole(String(item?.description || "").trim());
        const showDescription = !evidenceDescriptionIsRedundant(description, findingText, quote);

        return (
          <article key={`${timestamp}-${index}`} className="has-verbatim-quote">
            {timestampHref ? (
              <Link
                to={timestampHref}
                className="findings-key-moment__timestamp"
                title="Open this moment in the transcript"
                aria-label={`Open transcript at ${timestamp}`}
              >
                {timestamp}
              </Link>
            ) : (
              <span className="findings-key-moment__timestamp is-static">{timestamp}</span>
            )}
            <div className="findings-key-moment__evidence">
              <q className="findings-key-moment__quote evidence-verbatim-quote">{quote}</q>
              {showDescription && (
                <strong className="findings-key-moment__takeaway">{description}</strong>
              )}
            </div>
            <ConfChip confidence={item?.confidence} />
          </article>
        );
      })}
    </div>
  );
}

function EvidenceFindingCard({ item, timestampHrefBuilder, forceExpanded = false }) {
  const [expanded, setExpanded] = useState(forceExpanded);
  const finding = itemParts(item?.finding || item?.conclusion || item);
  const moments = Array.isArray(item?.supporting_moments)
    ? item.supporting_moments.filter((moment) => {
        const timestamp = moment?.timestamp || moment?.timestamp_estimate;
        const quote = String(moment?.quote || "").trim();
        const description = localizeSellerRole(String(moment?.description || "").trim());
        return timestamp
          && quote
          && quote.toLowerCase() !== "no direct quote"
          && !evidenceDescriptionIsRedundant(description, finding.text, quote);
      }).slice(0, 3)
    : [];

  useEffect(() => {
    if (forceExpanded) setExpanded(true);
  }, [forceExpanded]);

  if (!finding.text || !moments.length) return null;

  return (
    <article className={`findings-evidence-card${expanded ? " is-expanded" : " is-collapsed"}`}>
      <button
        type="button"
        className="findings-evidence-card__toggle"
        onClick={() => setExpanded((current) => !current)}
        aria-expanded={expanded}
      >
        <span className="findings-evidence-card__finding">
          <strong>{localizeSellerRole(finding.text)}</strong>
        </span>
        <span className="findings-evidence-card__controls">
          <ConfChip confidence={finding.confidence} />
          <CaretDown size={16} weight="bold" aria-hidden="true" />
        </span>
      </button>

      {expanded && (
        <div className="findings-evidence-card__details">
          <div className="findings-evidence-card__moments">
            <small>Supporting moments</small>
            <KeyMomentList
              items={moments}
              timestampHrefBuilder={timestampHrefBuilder}
              findingText={finding.text}
            />
          </div>
        </div>
      )}
    </article>
  );
}

function EvidenceSection({ id, title, icon: Icon, items, timestampHrefBuilder, forceExpanded = false }) {
  const rawItems = Array.isArray(items) ? items : items ? [items] : [];
  const visibleItems = rawItems.filter((item) => {
    const finding = itemParts(item?.finding || item?.conclusion || item);
    const moments = Array.isArray(item?.supporting_moments) ? item.supporting_moments : [];
    return finding.text && moments.some((moment) => {
      const timestamp = moment?.timestamp || moment?.timestamp_estimate;
      const quote = String(moment?.quote || "").trim();
      return timestamp && quote && quote.toLowerCase() !== "no direct quote";
    });
  });
  if (!visibleItems.length) return null;

  return (
    <section id={`deep-dive-${id}`} className="findings-evidence-section">
      <div className="findings-evidence-section__header">
        <Icon size={13} weight="fill" aria-hidden="true" />
        <h3>{title}</h3>
      </div>
      <div className="findings-evidence-grid">
        {visibleItems.map((item, index) => (
          <EvidenceFindingCard
            key={`${id}-${index}`}
            item={item}
            timestampHrefBuilder={timestampHrefBuilder}
            forceExpanded={forceExpanded}
          />
        ))}
      </div>
    </section>
  );
}

function AnalysisView({
  analysis,
  onRun,
  loading,
  findingsStep,
  relationshipPath,
  relationshipName,
  relationshipReturnState,
  timestampHrefBuilder,
  deepDiveTarget,
  onRepairDeepDive,
  repairingDeepDive,
  processingStatus,
}) {
  if (!analysis) {
    const normalizedProcessingStatus = String(processingStatus || "").toLowerCase();
    const automaticProcessing = ["queued", "transcribing", "analyzing"].includes(normalizedProcessingStatus);
    const retryableFailure = normalizedProcessingStatus === "partial";
    const unavailable = ["ready", "failed"].includes(normalizedProcessingStatus);
    const automaticProcessingMessage = normalizedProcessingStatus === "analyzing"
      ? "Callsect is still analyzing this conversation."
      : "Callsect is still processing this conversation.";

    if (automaticProcessing) {
      return (
        <section className="findings-empty-state">
          <div className="findings-empty-state__visual"><RevealLinesMark /></div>
          <div>
            <div className="brand-eyebrow">Conversation dissection</div>
            <h2>{automaticProcessingMessage}</h2>
          </div>
        </section>
      );
    }

    if (retryableFailure) {
      return (
        <section className="findings-empty-state">
          <div className="findings-empty-state__visual"><RevealLinesMark /></div>
          <div>
            <div className="brand-eyebrow">Conversation dissection</div>
            <h2>This analysis is temporarily unavailable.</h2>
          </div>
          <button onClick={onRun} disabled={loading} className="call-detail-primary-button">
            {loading ? <WorkingHourglass size={16} /> : <ArrowClockwise size={16} weight="bold" />} {loading ? "Retrying..." : "Retry analysis"}
          </button>
        </section>
      );
    }

    if (unavailable) {
      return (
        <section className="findings-empty-state">
          <div className="findings-empty-state__visual"><RevealLinesMark /></div>
          <div>
            <div className="brand-eyebrow">Conversation dissection</div>
            <h2>This analysis is temporarily unavailable.</h2>
          </div>
        </section>
      );
    }

    return (
      <section className="findings-empty-state">
        <div className="findings-empty-state__visual"><RevealLinesMark /></div>
        {loading ? (
          <div className="findings-processing findings-processing--centered">
            {["Scanning the conversation", "Connecting evidence"].map((label, index) => {
              const step = index + 1;
              const complete = findingsStep > step;
              const active = findingsStep === step;
              return (
                <div key={label} className={`findings-processing__step${active ? " is-active" : ""}${complete ? " is-complete" : ""}`}>
                  <span className="findings-processing__number">{complete ? "✓" : step}</span>
                  <span>{label}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div>
            <div className="brand-eyebrow">Conversation dissection</div>
            <h2>Reveal what really happened.</h2>
            <p>Surface the commercial status, real friction, agreed actions and next routes forward.</p>
          </div>
        )}
        <button onClick={onRun} disabled={loading} className="call-detail-primary-button">
          {loading ? <WorkingHourglass size={16} /> : <Target size={16} weight="fill" />} {loading ? "Generating findings..." : "Start dissection"}
        </button>
      </section>
    );
  }

  const status = analysis.commercial_status || {};
  const deep = analysis.deep_dive || {};
  const takeaway = itemParts(analysis.takeaway || analysis.summary);
  const stage = status.stage || analysis.negotiation_stage;
  const decisionMaker = status.decision_maker || status.decision_owner || analysis.decision_maker_influence;
  const keyMoments = deep.key_moments || analysis.key_moments || [];
  const signalMetrics = sortScoreMetrics([
    { label: "Buying intent", value: analysis.buying_intent, reason: analysis.score_reasons?.buying_intent, description: SIGNAL_DESCRIPTIONS.buyingIntent },
    { label: "Trust", value: analysis.trust_level, reason: analysis.score_reasons?.trust_level, description: SIGNAL_DESCRIPTIONS.trust },
    { label: "Relationship", value: analysis.relationship_strength, reason: analysis.score_reasons?.relationship_strength, description: SIGNAL_DESCRIPTIONS.relationship },
    { label: "Confidence", value: analysis.confidence_level, reason: analysis.score_reasons?.confidence_level, description: SIGNAL_DESCRIPTIONS.confidence },
    { label: "Churn risk", value: analysis.risk_of_churn, reason: analysis.score_reasons?.risk_of_churn, description: SIGNAL_DESCRIPTIONS.churnRisk, directionNote: "More = higher risk", invert: true },
    { label: "Quality", value: analysis.conversation_quality, reason: analysis.score_reasons?.conversation_quality, description: SIGNAL_DESCRIPTIONS.quality },
  ]);
  const evidenceSections = [
    { id: "stance", title: "Stance", icon: Handshake, items: deep.stance },
    { id: "tone", title: "Tone", icon: ChatCircle, items: deep.tone },
    { id: "blockers", title: "Blockers", icon: WarningCircle, items: deep.blockers },
    { id: "objections", title: "Objections", icon: Flag, items: deep.objections },
    { id: "risks", title: "Risks", icon: Warning, items: deep.risks },
    { id: "opportunities", title: "Opportunities", icon: Lightbulb, items: deep.opportunities },
  ];
  const hasEvidenceSections = evidenceSections.some(({ items }) => {
    const candidates = Array.isArray(items) ? items : items ? [items] : [];
    return candidates.some((item) => (
      itemParts(item?.finding || item?.conclusion || item).text
      && (
        (Array.isArray(item?.why) && item.why.length > 0)
        || (Array.isArray(item?.supporting_moments) && item.supporting_moments.length > 0)
      )
    ));
  });

  return (
    <div className="findings-view findings-view--classic" data-testid="analysis-view">
      <div className="findings-classic-grid">
        <section className="findings-signal-profile" style={{ animationDelay: "0ms" }}>
          <div className="findings-card__eyebrow">
            <ChartBar size={15} weight="fill" />
            Signal profile
          </div>
          <div className="findings-signal-profile__body">
            {signalMetrics.map((metric) => <Bar key={metric.label} {...metric} />)}
          </div>
        </section>

        <section className="findings-key-takeaway" style={{ animationDelay: "60ms" }}>
          <div className="findings-card__eyebrow">
            <MagnifyingGlass size={15} weight="fill" />
            Key takeaway
          </div>
          <h2 className="findings-key-takeaway__text">{localizeSellerRole(takeaway.text)}</h2>
          <div className="findings-takeaway-grid">
            <Kv label="Stage" val={stage} icon={TrendUp} />
            <Kv label="Decision maker" val={decisionMaker} icon={UserCircle} />
          </div>
        </section>
      </div>

      <section id="deep-dive" className="findings-deep-dive is-open">
        <div className="findings-deep-dive__header">
          <span>
            <Brain size={15} weight="fill" />
            DEEP DIVE
            <small>Why Callsect reached each conclusion</small>
          </span>
        </div>

        <div className="findings-deep-dive__content findings-deep-dive__content--evidence">
          {hasEvidenceSections ? (
            evidenceSections.map((section) => (
              <EvidenceSection
                key={section.id}
                {...section}
                timestampHrefBuilder={timestampHrefBuilder}
                forceExpanded={deepDiveTarget === section.id}
              />
            ))
          ) : (
            <div className="findings-deep-dive__legacy">
              <KeyMomentList items={keyMoments} timestampHrefBuilder={timestampHrefBuilder} />
              {onRepairDeepDive && (
                <button
                  type="button"
                  onClick={onRepairDeepDive}
                  disabled={repairingDeepDive}
                  className="call-detail-secondary-button"
                  data-testid="repair-deep-dive-btn"
                >
                  {repairingDeepDive ? <WorkingHourglass size={13} /> : <ArrowClockwise size={13} />} {repairingDeepDive ? "Repairing Deep Dive..." : "Repair Deep Dive"}
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Kv({ label, val, color = "text-slate-200", icon: Icon }) {
  if (!val) return null;
  const { text, confidence } = itemParts(val);
  if (!text || text === "not evident") return null;
  return (
    <div className="findings-kv">
      <span className="findings-kv__label">
        {Icon && <Icon size={13} weight="fill" aria-hidden="true" />}
        {label}
      </span>
      <strong className={color}>{text}<ConfChip confidence={confidence} inline /></strong>
    </div>
  );
}

function CoachingView({ coaching, onRun, loading, processingStatus, timestampHrefBuilder, transcriptSegments }) {
  if (!coaching) {
    const normalizedProcessingStatus = String(processingStatus || "").toLowerCase();
    const automaticProcessing = ["queued", "transcribing", "analyzing"].includes(normalizedProcessingStatus);
    const retryableFailure = normalizedProcessingStatus === "partial";
    const unavailable = ["ready", "failed"].includes(normalizedProcessingStatus);

    if (automaticProcessing || (loading && !retryableFailure)) {
      return (
        <section className="call-detail-empty-state call-detail-empty-state--large">
          <RevealLinesMark />
          <strong>Coaching will appear when the analysis is complete.</strong>
        </section>
      );
    }

    if (retryableFailure) {
      return (
        <section className="call-detail-empty-state call-detail-empty-state--large">
          <RevealLinesMark />
          <strong>This analysis is temporarily unavailable.</strong>
          <button onClick={onRun} disabled={loading} className="call-detail-primary-button">
            {loading ? <WorkingHourglass size={16} /> : <ArrowClockwise size={16} weight="bold" />} {loading ? "Retrying..." : "Retry analysis"}
          </button>
        </section>
      );
    }

    if (unavailable) {
      return (
        <section className="call-detail-empty-state call-detail-empty-state--large">
          <RevealLinesMark />
          <strong>This analysis is temporarily unavailable.</strong>
        </section>
      );
    }

    return (
      <section className="call-detail-empty-state call-detail-empty-state--large">
        <RevealLinesMark />
        <strong>No coaching yet.</strong>
        <p>Run the analysis to turn the conversation into practical coaching moves.</p>
        <button onClick={onRun} disabled={loading} className="call-detail-primary-button">
          <ChartBar size={16} weight="fill" /> Run analysis
        </button>
      </section>
    );
  }

  const skillMetrics = sortScoreMetrics([
    { label: "Empathy", value: coaching.empathy_score, reason: coaching.skill_reasons?.empathy_score, description: SKILL_DESCRIPTIONS.empathy },
    { label: "Listening", value: coaching.listening_score, reason: coaching.skill_reasons?.listening_score, description: SKILL_DESCRIPTIONS.listening },
    { label: "Conversation control", value: coaching.control_of_conversation, reason: coaching.skill_reasons?.control_of_conversation, description: SKILL_DESCRIPTIONS.conversationControl },
  ]);
  const speakerResolver = (evidence) => coachingSpeakerForEvidence(transcriptSegments, evidence);
  const hasWhatWorked = Array.isArray(coaching.what_worked) && coaching.what_worked.length > 0;
  const hasCoachingMoves = Array.isArray(coaching.coaching_moves) && coaching.coaching_moves.length > 0;
  const hasNextConversation = [
    coaching.missed_opportunities,
    coaching.questions_to_ask,
    coaching.negotiation_suggestions,
  ].some((items) => Array.isArray(items) && items.length > 0);

  return (
    <div className="coaching-view coaching-view--structured" data-testid="coaching-view">
      <div className="coaching-overview-grid">
        <section className="coaching-card coaching-card--skills" style={{ animationDelay: "0ms" }}>
          <div className="findings-card__eyebrow"><ChartBar size={15} weight="fill" /> Skills</div>
          <div className="coaching-card__body">
            {skillMetrics.map((metric) => <Bar key={metric.label} {...metric} />)}
          </div>
        </section>

        <section className="coaching-card" style={{ animationDelay: "70ms" }}>
          <div className="findings-card__eyebrow"><CheckCircle size={15} weight="fill" /> What worked</div>
          <div className="coaching-card__body">
            {hasWhatWorked ? (
              <List title="Strengths to retain" items={coaching.what_worked} icon={Barbell} color="text-emerald-300" italicQuotes showTimestamps timestampHrefBuilder={timestampHrefBuilder} speakerResolver={speakerResolver} />
            ) : (
              <p className="findings-section-card__empty coaching-empty-result">No standout strengths identified in this call.</p>
            )}
          </div>
        </section>
      </div>

      <section className="coaching-card coaching-card--moves" style={{ animationDelay: "140ms" }}>
        <div className="findings-card__eyebrow"><Megaphone size={15} weight="fill" /> Coaching moves</div>
        <div className="coaching-moves">
          {!hasCoachingMoves && (
            <p className="findings-section-card__empty coaching-empty-result">No high-impact coaching move identified.</p>
          )}
          {coaching.coaching_moves?.map((move, index) => (
            <article key={index} className="coaching-move">
              <div className="coaching-move__header"><strong>{localizeSellerRole(move.title)}</strong><ConfChip confidence={move.confidence} /></div>
              <div className="coaching-move__section coaching-move__section--guidance-label">
                <span>Situation</span>
                <div className="coaching-move__evidence">
                  <span className="findings-list__bullet coaching-move__bullet" aria-hidden="true" />
                  <span
                    className={`coaching-evidence__line coaching-move__evidence-line${
                      String(move.timestamp || "").trim() ? " coaching-evidence__line--with-meta" : ""
                    }`}
                  >
                    <CoachingEvidenceMeta
                      speaker={speakerResolver(move)}
                      timestamp={move.timestamp}
                      href={typeof timestampHrefBuilder === "function" ? timestampHrefBuilder(move.timestamp) : null}
                    />
                    {(() => {
                      const situation = localizeSellerRole(move.situation);
                      const { quote, commentary } = splitLeadingEvidenceQuote(situation);
                      return (
                        <span className="coaching-move__situation-copy">
                          <blockquote className="evidence-verbatim-quote coaching-move__situation">
                            {quote || `“${situation}”`}
                          </blockquote>
                          {quote && commentary && <p className="coaching-move__situation-commentary">{commentary}</p>}
                        </span>
                      );
                    })()}
                  </span>
                </div>
              </div>
              <div className="coaching-move__section coaching-move__section--continuation coaching-move__section--guidance-label"><span>Better approach</span><p>{renderWithItalicQuotes(localizeSellerRole(move.better_move))}</p></div>
              <div className="coaching-move__section coaching-move__section--continuation"><span>Example wording</span><blockquote className="is-example">“{localizeSellerRole(move.example_phrase)}”</blockquote></div>
              <div className="coaching-move__section coaching-move__section--continuation coaching-move__section--guidance-label"><span>Why it works</span><p>{renderWithItalicQuotes(localizeSellerRole(move.why_it_works))}</p></div>
            </article>
          ))}
        </div>
      </section>

      <section className="coaching-card coaching-card--wide" style={{ animationDelay: "210ms" }}>
        <div className="findings-card__eyebrow"><Target size={15} weight="fill" /> Next conversation</div>
        <div className="coaching-card__columns">
          {hasNextConversation ? (
            <>
              <List title="Missed opportunities" items={coaching.missed_opportunities} icon={ArrowCircleUpRight} italicQuotes showTimestamps timestampHrefBuilder={timestampHrefBuilder} speakerResolver={speakerResolver} />
              <List title="Questions to ask" items={coaching.questions_to_ask} icon={ChatsCircle} questionPrompt showTimestamps timestampHrefBuilder={timestampHrefBuilder} speakerResolver={speakerResolver} />
              <List title="Negotiation suggestions" items={coaching.negotiation_suggestions} icon={Handshake} italicQuotes />
            </>
          ) : (
            <p className="findings-section-card__empty coaching-empty-result">No additional next-conversation actions identified.</p>
          )}
        </div>
      </section>
    </div>
  );
}
